import React, { ChangeEvent, FC, PropsWithChildren, useCallback, useEffect, useState, useReducer } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { action } from 'typesafe-actions';
import { LoadingProgressTakeover } from '@dxb-web-ui-framework/common/components/LoadingProgress';
import { devicesService } from '../../../../../channels/secure-web/services';
import { DeviceData } from '../../domain/Device/entities';
import { DeviceManagementContext, DeviceManagementDispatchContext } from './DeviceManagementContext';
import { defaultDeviceManagementState } from './defaultState';
import { DeviceManagementReducer } from './DeviceManagementReducer';
import { DeviceManagementContextActionTypes, DeviceManagementProviderProps } from './types';

const DeviceManagementProvider: FC<PropsWithChildren<DeviceManagementProviderProps>> = ({ children }) => {
  // Get initial state values
  const [state, dispatch] = useReducer(DeviceManagementReducer, defaultDeviceManagementState);
  const [timestamp, setTimestamp] = useState(new Date().toISOString());

  // Define api calls
  // Get devicesData
  const {
    data: devicesData,
    isLoading: isLoadingDevicesData,
    error: devicesDataError,
    mutate: refetchDevicesData,
  } = useInvokeSWRService<DeviceData>(
    devicesService?.GetDevices,
    { ...state.paginationSettings, meta: { timestamp } },
    true,
    'devicesService.GetDevices',
  );

  useEffect(() => {
    if (devicesData) {
      dispatch(
        action(DeviceManagementContextActionTypes.SetPaginationResult, {
          totalResults: devicesData.totalResults,
          totalPages: Math.ceil(devicesData.totalResults / state.paginationSettings.pageSize),
        }),
      );
    }
  }, [devicesData, state.paginationSettings.pageSize]);

  const onPageChange = useCallback(
    (event: ChangeEvent<unknown>, page: number) => {
      dispatch(
        action(DeviceManagementContextActionTypes.SetCurrentPage, Math.min(page, state.paginationResult.totalPages)),
      );
      dispatch(
        action(DeviceManagementContextActionTypes.SetPaginationSettings, {
          pageOffset: Math.min(Math.max(page - 1, 0), state.paginationResult.totalPages - 1),
        }),
      );
      setTimestamp(new Date().toISOString());
      window.scrollTo(0, 0);
    },
    [state.paginationResult.totalPages],
  );

  const setSelectedDevice = useCallback(device => {
    dispatch(action(DeviceManagementContextActionTypes.SetSelectedDevice, device));
  }, []);

  const onResetSelectedDevice = useCallback(() => {
    dispatch(action(DeviceManagementContextActionTypes.ResetSelectedDevice, null));
  }, []);

  const getDevicesData = useCallback(() => {
    refetchDevicesData && refetchDevicesData();
  }, [refetchDevicesData]);

  const shouldShowChildren = !!devicesService;
  return (
    <DeviceManagementContext.Provider
      value={{
        ...state,
        devicesData,
        loadingState: {
          isLoadingDevicesData,
        },
        errorState: {
          devicesDataError,
        },
        onPageChange,
        setSelectedDevice,
        onResetSelectedDevice,
        getDevicesData,
      }}>
      <DeviceManagementDispatchContext.Provider value={dispatch}>
        {shouldShowChildren ? children : <LoadingProgressTakeover />}
      </DeviceManagementDispatchContext.Provider>
    </DeviceManagementContext.Provider>
  );
};

export { DeviceManagementProvider };
